import {
  useCallback, useEffect, useMemo, useReducer, useRef,
} from 'react';
import { message } from 'antd';
import { useActions } from '../../../providers/actions-provider/actions.context';
import {
  alignPropertyNames,
  buildStartJobRequest,
  detectMapping,
  IImportPlan,
  isJsonFile,
  jsonFileToCsv,
  IPrefixSample,
  samplePrefix,
  sampleIsThin,
  validateImportFile,
} from '../../../../utils/asset-type-import';
import {
  MAX_PROBLEM_ROWS, PROBLEM_PAGE_SIZE,
} from '../../../../utils/asset-type-import/constants';
import {
  IImportJob, IImportProblem, isLiveJob, isTerminalJob,
} from '../../../../utils/asset-type-import/i-import-job';
import { uploadInParts } from '../../../../utils/chunk-upload';
import { IUploadProgress, TWizardStep } from './i-import-wizard';
import { useImportJob } from './use-import-job';

export interface IWizardState {
  step: TWizardStep;
  /** what actually gets uploaded — the original CSV, or JSON converted to one */
  file: Blob | null;
  fileName: string;
  fileBytes: number;
  fileError: string | null;
  prefix: IPrefixSample | null;
  plan: IImportPlan | null;
  propertyNames: string[];
  estimatedRows: number | null;
  uploadId: string | null;
  upload: IUploadProgress | null;
  jobId: string | null;
  problems: IImportProblem[];
  problemPage: number;
  problemsLoading: boolean;
  downloading: boolean;
  busy: boolean;
  /** a job still running from a previous visit, offered on the upload step */
  resumable: IImportJob | null;
}

type TWizardAction =
  | { type: 'fileError'; reason: string }
  | {
    type: 'fileReady';
    file: Blob; fileName: string; fileBytes: number; prefix: IPrefixSample;
    plan: IImportPlan; estimatedRows: number | null;
  }
  | { type: 'setPlan'; plan: IImportPlan }
  | { type: 'setPropertyNames'; names: string[] }
  | { type: 'setResumable'; job: IImportJob | null }
  | { type: 'uploadStarted'; uploadId: string }
  | { type: 'uploadProgress'; upload: IUploadProgress }
  | { type: 'jobStarted'; jobId: string }
  | { type: 'problemsLoading'; loading: boolean }
  | { type: 'problems'; rows: IImportProblem[]; page: number }
  | { type: 'downloading'; downloading: boolean }
  | { type: 'busy'; busy: boolean }
  | { type: 'goTo'; step: TWizardStep }
  | { type: 'reset' };

export const initialState: IWizardState = {
  step: 'upload',
  file: null,
  fileName: '',
  fileBytes: 0,
  fileError: null,
  prefix: null,
  plan: null,
  propertyNames: [],
  estimatedRows: null,
  uploadId: null,
  upload: null,
  jobId: null,
  problems: [],
  problemPage: 1,
  problemsLoading: false,
  downloading: false,
  busy: false,
  resumable: null,
};

export const wizardReducer = (state: IWizardState, action: TWizardAction): IWizardState => {
  switch (action.type) {
    case 'fileError':
      return {
        ...initialState,
        propertyNames: state.propertyNames,
        resumable: state.resumable,
        fileError: action.reason,
      };
    case 'fileReady':
      return {
        ...state,
        file: action.file,
        fileName: action.fileName,
        fileBytes: action.fileBytes,
        prefix: action.prefix,
        plan: alignPropertyNames(action.plan, state.propertyNames),
        estimatedRows: action.estimatedRows,
        fileError: null,
        busy: false,
        // always stop at the mapping step, even when detection is confident:
        // a multi-minute job should not start on a guess the user never saw
        step: 'map',
      };
    case 'setPlan':
      return { ...state, plan: action.plan };
    case 'setPropertyNames':
      return {
        ...state,
        propertyNames: action.names,
        plan: state.plan ? alignPropertyNames(state.plan, action.names) : state.plan,
      };
    case 'setResumable':
      return { ...state, resumable: action.job };
    case 'uploadStarted':
      return { ...state, uploadId: action.uploadId, step: 'running' };
    case 'uploadProgress':
      return { ...state, upload: action.upload };
    case 'jobStarted':
      return {
        ...state, jobId: action.jobId, upload: null, busy: false, step: 'running',
      };
    case 'problemsLoading':
      return { ...state, problemsLoading: action.loading };
    case 'problems':
      return {
        ...state, problems: action.rows, problemPage: action.page, problemsLoading: false,
      };
    case 'downloading':
      return { ...state, downloading: action.downloading };
    case 'busy':
      return { ...state, busy: action.busy };
    case 'goTo':
      return { ...state, step: action.step };
    case 'reset':
      return { ...initialState, propertyNames: state.propertyNames };
    default:
      return state;
  }
};

export const planIsComplete = (plan: IImportPlan | null): boolean => {
  if (!plan || plan.codeColumn < 0 || plan.descriptionColumn < 0) {
    return false;
  }
  const names = plan.propertyColumns.map((column) => column.name.trim().toLowerCase());
  return names.every((name) => name !== '') && new Set(names).size === names.length;
};

/** Rough row count from the prefix, so the upload step can say "about 195 000". */
export const estimateRows = (sample: IPrefixSample, totalBytes: number, prefixBytes: number)
: number | null => {
  if (sample.whole) {
    return sample.rows.length;
  }
  if (sample.rows.length === 0) {
    return null;
  }
  const bytesPerRow = prefixBytes / sample.rows.length;
  return Math.round(totalBytes / bytesPerRow);
};

export interface IUseImportAssetTypes {
  state: IWizardState;
  job: IImportJob | null;
  pollError: string | null;
  samples: string[];
  advisories: string[];
  canContinueFromMap: boolean;
  onFileSelected: (file: File) => void;
  onPlanChange: (plan: IImportPlan) => void;
  onResume: (jobId: string) => void;
  startImport: () => Promise<void>;
  confirmImport: () => Promise<void>;
  cancelJob: () => Promise<void>;
  loadProblems: (page: number) => Promise<void>;
  downloadProblems: () => Promise<void>;
  retryPoll: () => void;
  goTo: (step: TWizardStep) => void;
  reset: () => void;
}

export const useImportAssetTypes = (): IUseImportAssetTypes => {
  const [state, dispatch] = useReducer(wizardReducer, initialState);
  const { assetActions } = useActions();
  const mounted = useRef(true);

  // see use-import-job: assetActions' identity changes on every store dispatch
  const actions = useRef(assetActions);
  actions.current = assetActions;

  const { job, pollError, retry } = useImportJob(state.jobId);

  useEffect(() => () => {
    mounted.current = false;
  }, []);

  /* property names drive the casing reconciliation on the mapping step */
  useEffect(() => {
    actions.current.fetchAssetTypePropertyNames()
      .then((names) => {
        if (mounted.current) {
          dispatch({ type: 'setPropertyNames', names });
        }
      })
      .catch(() => undefined);
  }, []);

  /* offer to re-attach to a job left running by a previous visit */
  useEffect(() => {
    actions.current.listImportJobs('assetTypes', 5)
      .then((jobs) => {
        const live = jobs.find((candidate) => isLiveJob(candidate.state));
        if (mounted.current && live) {
          dispatch({ type: 'setResumable', job: live });
        }
      })
      .catch(() => undefined);
  }, []);

  const loadProblems = useCallback(async (page: number): Promise<void> => {
    if (!state.jobId) {
      return;
    }
    dispatch({ type: 'problemsLoading', loading: true });
    try {
      const skip = (page - 1) * PROBLEM_PAGE_SIZE;
      const result = await actions.current.fetchImportProblems(
        state.jobId, skip, PROBLEM_PAGE_SIZE,
      );
      if (mounted.current) {
        dispatch({ type: 'problems', rows: result?.rows ?? [], page });
      }
    } catch {
      if (mounted.current) {
        dispatch({ type: 'problemsLoading', loading: false });
      }
    }
  }, [state.jobId]);

  /*
   * Pull the first page of problems as soon as there is something to show, and
   * again when the job settles — phase B adds duplicates and failures that
   * phase A could not know about.
   */
  const lastLoadedFor = useRef<string>('');
  useEffect(() => {
    if (!job) {
      return;
    }
    const key = `${job.jobId}:${job.state}:${job.errorCount}`;
    const interesting = job.state === 'validated' || isTerminalJob(job.state);
    if (interesting && job.errorCount > 0 && lastLoadedFor.current !== key) {
      lastLoadedFor.current = key;
      loadProblems(1);
    }
  }, [job, loadProblems]);

  const onFileSelected = useCallback((file: File): void => {
    const check = validateImportFile(file);
    if (!check.ok) {
      dispatch({ type: 'fileError', reason: check.reason });
      return;
    }

    dispatch({ type: 'busy', busy: true });

    const prepare = async (): Promise<void> => {
      // JSON is converted here, not on the server: its header is derived from
      // the rows it sees, so a sample and a full pass would disagree
      const upload = isJsonFile(file.name) ? (await jsonFileToCsv(file)).blob : file;
      const prefix = await samplePrefix(upload);

      if (prefix.header.length === 0) {
        throw new Error(prefix.errors[0]?.message ?? 'The file has no readable rows.');
      }

      return dispatch({
        type: 'fileReady',
        file: upload,
        fileName: file.name,
        fileBytes: upload.size,
        prefix,
        plan: detectMapping(prefix.header),
        estimatedRows: estimateRows(prefix, upload.size, Math.min(upload.size, 512 * 1024)),
      });
    };

    prepare().catch((error: Error) => {
      if (mounted.current) {
        dispatch({ type: 'fileError', reason: error.message || 'Could not read the file.' });
      }
    });
  }, []);

  const startImport = useCallback(async (): Promise<void> => {
    const {
      file, fileName, plan, prefix,
    } = state;
    if (!file || !plan || !prefix) {
      return;
    }

    dispatch({ type: 'busy', busy: true });

    try {
      const created = await actions.current.createUpload(fileName, file.size);
      dispatch({ type: 'uploadStarted', uploadId: created.uploadId });

      await uploadInParts({
        file,
        partBytes: created.partSize,
        alreadyReceived: created.receivedParts,
        putPart: (index, chunk, onPartProgress) => actions.current.uploadPart(
          created.uploadId,
          index,
          chunk,
          (event) => onPartProgress(event.loaded),
        ),
        onProgress: (progress) => {
          if (mounted.current) {
            dispatch({ type: 'uploadProgress', upload: progress });
          }
        },
      });

      await actions.current.completeUpload(created.uploadId);

      const request = buildStartJobRequest(plan, prefix.header, prefix.delimiter);
      const started = await actions.current.startAssetTypeImportJob(created.uploadId, request);

      if (mounted.current) {
        dispatch({ type: 'jobStarted', jobId: started.jobId });
      }
    } catch (error) {
      if (mounted.current) {
        dispatch({ type: 'busy', busy: false });
      }
      message.error('Could not start the import. Nothing was created.');
    }
  }, [state]);

  const confirmImport = useCallback(async (): Promise<void> => {
    if (!state.jobId) {
      return;
    }
    dispatch({ type: 'busy', busy: true });
    try {
      await actions.current.confirmAssetTypeImportJob(state.jobId);
    } catch {
      message.error('Could not start the import.');
    } finally {
      if (mounted.current) {
        dispatch({ type: 'busy', busy: false });
      }
    }
  }, [state.jobId]);

  /**
   * Cancel means two different things, and the server says which happened.
   *
   * At the confirmation gate there is no worker to ask, so the response comes
   * back already cancelled and the wizard starts over on the spot. While the
   * file is being read or imported the job can only be asked to stop, so the
   * screen stays put and the poll reports the outcome a moment later. [AVP-901]
   */
  const cancelJob = useCallback(async (): Promise<void> => {
    if (!state.jobId) {
      // still uploading: the bytes only exist in the browser, so dropping the
      // staged upload is all it takes
      if (state.uploadId) {
        await actions.current.discardUpload(state.uploadId);
      }
      dispatch({ type: 'reset' });
      return;
    }

    dispatch({ type: 'busy', busy: true });
    try {
      const outcome = await actions.current.cancelImportJob(state.jobId);
      // only a job that wrote nothing may be swept away silently: one that got
      // as far as creating rows has a result the user still needs to see
      const wroteNothing = outcome.created === 0 && outcome.updated === 0;
      if (mounted.current && outcome.state === 'cancelled' && wroteNothing) {
        message.success('Import cancelled. Nothing was imported.');
        dispatch({ type: 'reset' });
        return;
      }
    } catch {
      message.error('Could not cancel the import.');
    }
    if (mounted.current) {
      dispatch({ type: 'busy', busy: false });
    }
  }, [state.jobId, state.uploadId]);

  const downloadProblems = useCallback(async (): Promise<void> => {
    if (!state.jobId) {
      return;
    }
    dispatch({ type: 'downloading', downloading: true });
    try {
      await actions.current.downloadImportProblems(state.jobId);
    } catch {
      message.error('Could not download the report.');
    } finally {
      if (mounted.current) {
        dispatch({ type: 'downloading', downloading: false });
      }
    }
  }, [state.jobId]);

  const samples = useMemo(() => {
    const header = state.prefix?.header ?? [];
    return header.map((unused, columnIndex) => {
      const found = (state.prefix?.rows ?? []).find((cells) => (cells[columnIndex] ?? '') !== '');
      return found ? found[columnIndex] : '';
    });
  }, [state.prefix]);

  const advisories = useMemo(() => {
    const notes: string[] = [];
    if (state.prefix && sampleIsThin(state.prefix)) {
      notes.push(
        'We could not read a useful sample from this file — check the column mapping carefully.',
      );
    }
    (state.plan?.warnings ?? []).forEach((warning) => notes.push(warning));
    return notes.slice(0, 3);
  }, [state.prefix, state.plan]);

  return {
    state,
    job,
    pollError,
    samples,
    advisories,
    canContinueFromMap: planIsComplete(state.plan) && !state.busy,
    onFileSelected,
    onPlanChange: useCallback((plan: IImportPlan) => dispatch({ type: 'setPlan', plan }), []),
    onResume: useCallback((jobId: string) => dispatch({ type: 'jobStarted', jobId }), []),
    startImport,
    confirmImport,
    cancelJob,
    loadProblems,
    downloadProblems,
    retryPoll: retry,
    goTo: useCallback((step: TWizardStep) => dispatch({ type: 'goTo', step }), []),
    reset: useCallback(() => dispatch({ type: 'reset' }), []),
  };
};

export const MAX_SHOWN_PROBLEMS = MAX_PROBLEM_ROWS;
